import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../../api.ts';
import { formatEuros } from '../../../format.ts';
import { fmtDay, toCents, SOURCE_LABELS, type Client, type Revenue } from './types.ts';

const ERRORS: Record<string, string> = {
  MISSING_FIELDS: 'Choisissez un client.',
  CLIENT_NOT_FOUND: 'Choisissez un client.',
  INVALID_AMOUNT: 'Montant invalide.',
  INVALID_DATE: 'Date invalide : elle ne peut pas être dans le futur.',
  REVENUE_NOT_DELETABLE: 'Seules les recettes saisies à la main peuvent être supprimées.',
  PERIOD_ALREADY_DECLARED: 'Cette période est déjà déclarée à l’URSSAF : la recette ne peut plus être supprimée.',
};

const today = () => new Date().toISOString().slice(0, 10);

export function Revenues() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<{ revenues: Revenue[]; totalCents: number } | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ clientId: '', amount: '', receivedAt: today(), reference: '' });
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get<{ revenues: Revenue[]; totalCents: number }>(`/extra/revenues?year=${year}`).then(setData);
    api.get<{ clients: Client[] }>('/extra/clients').then((r) => setClients(r.clients));
  }, [year]);
  useEffect(load, [load]);

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post('/extra/revenues', { clientId: form.clientId, amountCents: toCents(form.amount), receivedAt: form.receivedAt, reference: form.reference || undefined });
      setForm({ clientId: '', amount: '', receivedAt: today(), reference: '' });
      setOpen(false);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Enregistrement impossible.' : 'Enregistrement impossible.');
    } finally {
      setBusy(false);
    }
  }

  async function remove(r: Revenue) {
    if (!window.confirm(`Supprimer la recette de ${formatEuros(r.amountCents)} (${r.clientName}) ?`)) return;
    setError(null);
    try {
      await api.delete(`/extra/revenues/${r.id}`);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? ERRORS[err.code] ?? 'Suppression impossible.' : 'Suppression impossible.');
    }
  }

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  return (
    <>
      <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Votre registre des recettes : une ligne par encaissement, à la date où vous êtes payé. Les missions Mon Extra et les factures hors app marquées « payées »
        y entrent automatiquement. Ajoutez ici les ventes dont la facture a été établie ailleurs.
      </div>
      {error && <div className="error-banner">{error}</div>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <select className="input" style={{ width: 'auto' }} value={year} onChange={(e) => setYear(Number(e.target.value))} aria-label="Année">
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <a className="btn btn-secondary btn-sm" style={{ display: 'inline-flex' }} href={`/api/extra/revenues/export.csv?year=${year}`}>Exporter en CSV</a>
        {!open && <button className="btn btn-primary btn-sm" onClick={() => setOpen(true)}>Ajouter une recette hors app</button>}
      </div>

      {open && (
        <form onSubmit={add} className="card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ fontWeight: 800 }}>Recette encaissée hors app</div>
          <select className="input" required value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
            <option value="">Client…</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {clients.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>Ajoutez d'abord un client dans l'onglet « Clients ».</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <input className="input" required inputMode="decimal" placeholder="Montant HT encaissé (€)" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
            <input className="input" type="date" required max={today()} value={form.receivedAt} onChange={(e) => setForm({ ...form, receivedAt: e.target.value })} aria-label="Date d'encaissement" />
          </div>
          <input className="input" placeholder="Référence ou n° de facture (facultatif)" value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-primary btn-sm" disabled={busy}>Enregistrer</button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(false)}>Annuler</button>
          </div>
        </form>
      )}

      {data && (
        <>
          <div className="card stat-card">
            <div className="stat-value">{formatEuros(data.totalCents)}</div>
            <div className="stat-label">Encaissé en {year}</div>
          </div>
          {data.revenues.length === 0 && <div className="empty-state">Aucune recette en {year}.</div>}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {data.revenues.map((r) => (
              <div key={r.id} className="card" style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800 }}>{r.clientName}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {fmtDay(r.receivedAt)} · {SOURCE_LABELS[r.source]}{r.invoiceNumber ? ` · ${r.invoiceNumber}` : r.reference ? ` · ${r.reference}` : ''}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <strong>{formatEuros(r.amountCents)}</strong>
                  {r.source === 'EXTERNAL' && <button className="btn btn-danger-outline btn-sm" onClick={() => remove(r)}>Supprimer</button>}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
